// Let's put utils here that we want to share with the backend, happy to change the name or move it to a library...

import { EventWithMembership } from '@modules/supabase/events';
import {
    Class,
    ClassToInsert,
    COACH_ROLE,
    Event,
    EVENT_ROLE,
    EventMembership,
    TEAM_ROLE,
    User,
} from '@type/db-types';
import { FieldErrors } from 'react-hook-form';

export const isEventAdmin = (
    user: User | null | undefined,
    createdByUserId: Event['createdByUserId'] | null | undefined,
    userEventMembership: Partial<EventMembership> | null | undefined,
): boolean => {
    if (!user || !createdByUserId) return false;

    return !!(
        user?.id === createdByUserId ||
        (userEventMembership?.role &&
            [EVENT_ROLE.STAFF, EVENT_ROLE.OWNER, EVENT_ROLE.PAYMENT_ADMIN].includes(
                userEventMembership.role,
            ))
    );
};

export const hidePlaceholderEmail = (email: string | null | undefined): string =>
    email && email.includes('@placeholder.com') ? '' : email || '';

export const isTeamCoach = (role: TEAM_ROLE | undefined): boolean => {
    if (!role) return false;

    return COACH_ROLE.includes(role);
};

export const isTeamOwner = (role: TEAM_ROLE | undefined): boolean => {
    if (!role) return false;

    return !!(role === TEAM_ROLE.OWNER);
};

export const getDivisionByAge = (age: number, { divisions = [] }: EventWithMembership) => {
    return divisions.find(
        (division) =>
            division.maxAge && division.minAge && age >= division.minAge && age <= division.maxAge,
    );
};

// NOTE: class.maxWeight needs to be a string to accommodate units like "Kg" or "Lbs" or no numbers at all like "Heavyweight"
export function getClassByAge<T extends Class | ClassToInsert>(
    weight: number,
    classes: T[] | undefined,
): T | undefined {
    // TODO: this classes sort should be on the backend SQL query
    // classes should be sorted by maxWeight, numbers first, then strings last
    return classes
        ?.sort(
            (a, b) =>
                parseInt(a.maxWeight || '0') - parseInt(b.maxWeight || '0') ||
                (a.maxWeight || '').localeCompare(b.maxWeight || ''),
        )
        .find(({ maxWeight }) => weight < parseInt(maxWeight || '0'));
}

export const isStringNumeric = (str: string | null): boolean => !Number.isNaN(Number(str));

/**
 * Extracts the error message from a FieldError object or array of FieldError objects
 * Useful for displaying error messages from react-hook-form as a string such as in a ToolTip
 */
export function getMessageFromError<T extends object>(
    error: FieldErrors<T> | FieldErrors<T>[] | undefined | string,
): string {
    if (!error) return '';
    else if (Array.isArray(error)) {
        return error.map(getMessageFromError).join('\n');
    } else if (error && typeof error === 'string') {
        return error;
    } else if (error && typeof error === 'object' && 'message' in error) {
        if (!error.message) return '';
        const indexes = (error.message as string).match(/\[\d+\]\./g);
        if (!indexes) return error.message as string;
        return (error.message as string)
            .replace(/\[\d+\]\./g, ` #${Number(indexes[0].replace(/\[|\D+\./g, '')) + 1} `)
            .replace(/\]\./g, ' ');
    } else {
        if (typeof error === 'object') {
            return (
                Object.keys(error)
                    // @ts-expect-error deeply nested error object
                    .map((key: keyof typeof error) => getMessageFromError(error[key]))
                    .join('\n')
            );
        } else {
            // FIXME: Does this ever happen?
            return '';
        }
    }
}
